// --------------[Utilities]----------------- //

function debounce(func, wait, immediate) {
  let timeout

  return function executedFunction() {
    const context = this
    const args = arguments

    const later = function() {
      timeout = null
      if (!immediate) func.apply(context, args)
    }

    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

function findMatchedElems(elems, categoryKey) {
  let matchedElems = []

  for (let i = 0; i < elems.length; i++) {
    let elem = elems[i]
    const matchedCategoryKey = elem.getAttribute('data-js-category-key')

    if (matchedCategoryKey === categoryKey) {
      matchedElems.push(elem)
    }
  }

  return matchedElems
}

function isHovering(items) {
  return items.some(hoverable =>
    hoverable.matches
      ? hoverable.matches(':hover')
      : hoverable.msMatchesSelector(':hover')
  )
}

function isFocusing(items) {
  return items.some(hoverable =>
    hoverable.matches
      ? hoverable.matches(':hover')
      : hoverable.msMatchesSelector(':hover')
  )
}

// --------------[Init Vars]----------------- //

let isAuthOpen = false
let isFlyoutOpen = false
let isSearchOpen = false
let isDropdownOpen = false

const htmlElement = document.getElementsByTagName('html')[0]
const desktopMatcher = window.matchMedia('(min-width: 1439px)')
const navOverlay = document.querySelector('[data-js-selector="nav-overlay"]')
const hamburger = document.querySelector('[data-js-selector="nav-hamburger"]')

const navCategoriesWrap = document.querySelector(
  '[data-js-selector="nav-categories"]'
)

const backToMainBtns = document.querySelectorAll(
  '[data-js-selector="back-to-main"]'
)

const navCategories = document.querySelectorAll(
  '[data-js-selector="nav-category-item"]'
)

const navCategoryFlyouts = document.querySelectorAll(
  '[data-js-selector="nav-category-flyout"]'
)

const navSubcategoryItems = document.querySelectorAll(
  '[data-js-selector="nav-subcategory-item"]'
)

const authDropdownToggle = document.querySelector(
  '[data-js-selector="toggle-auth-dropdown"]'
)

const authDropdown = document.querySelector(
  '[data-js-selector="auth-dropdown"]'
)

const globalSearchFilter = document.querySelector(
  '[data-js-selector="global-search-filter"]'
)

const searchToggle = document.querySelector(
  '[data-js-selector="search-toggle"]'
)

const searchSubmit = document.querySelector(
  '[data-js-selector="nav-search-submit-btn"]'
)

const searchInput = document.querySelector('#siteSearchInput')

// --------------[Nav Operations]----------------- //

function openFlyout(event, currentTarget, isDebounced) {
  // Don't open if the user already moused out before event is triggered
  if (isDebounced && !isHovering([...navCategories, ...navCategoryFlyouts])) {
    return
  }

  // Need this since currentTarget is lost when debouncing
  currentTarget = currentTarget || event.currentTarget

  if (event.type === 'click') {
    event.preventDefault()
  }

  closeAllNavMenus()
  closeAuthDropdown()

  const categoryKey = currentTarget.getAttribute('data-js-category-key')
  const matchedFlyouts = findMatchedElems(navCategoryFlyouts, categoryKey)
  const matchedCategories = findMatchedElems(navCategories, categoryKey)

  if (matchedFlyouts.length) {
    isFlyoutOpen = true
    hamburger.classList.add('--active')
    navOverlay.classList.add('--visible')
    matchedFlyouts[0].classList.add('--visible')
    hideCategoryDropdown()
  }

  // Apply active class for desktop mega menu styling
  if (matchedCategories.length) {
    matchedCategories[0].classList.add('--active')
  }
}

function closeFlyout(event) {
  const categoryKey = event.currentTarget.getAttribute('data-js-category-key')
  const matchedFlyouts = findMatchedElems(navCategoryFlyouts, categoryKey)

  if (matchedFlyouts.length) {
    isFlyoutOpen = false
    matchedFlyouts[0].classList.remove('--visible')
  }

  showCategoryDropdown()
}

function showCategoryDropdown() {
  isDropdownOpen = true
  navCategoriesWrap.classList.add('--visible')
}

function hideCategoryDropdown() {
  isDropdownOpen = false
  navCategoriesWrap.classList.remove('--visible')
}

function toggleDropdown() {
  if (isDropdownOpen || isFlyoutOpen) {
    hamburger.classList.remove('--active')
    htmlElement.style.overflow = ''
    return closeAllNavMenus()
  }

  hamburger.classList.add('--active')
  htmlElement.style.overflow = 'hidden'
  openDropdown()
}

function closeAllNavMenus() {
  isFlyoutOpen = false
  isDropdownOpen = false

  hamburger.classList.remove('--active')
  navOverlay.classList.remove('--visible')
  navCategoriesWrap.classList.remove('--visible')

  for (let i = 0; i < navCategoryFlyouts.length; i++) {
    navCategoryFlyouts[i].classList.remove('--visible')
  }

  // Remove active class from category button (this only affects desktop)
  for (let i = 0; i < navCategories.length; i++) {
    navCategories[i].classList.remove('--active')
  }
}

function openDropdown() {
  closeSearch()
  closeAuthDropdown()

  isDropdownOpen = true

  navOverlay.classList.add('--visible')
  navCategoriesWrap.classList.add('--visible')
}

function toggleSearch() {
  if (isSearchOpen) {
    return closeSearch()
  }

  return openSearch()
}

function openSearch() {
  isSearchOpen = true

  closeAllNavMenus()
  closeAuthDropdown()

  searchToggle.classList.add('--is-active')
  globalSearchFilter.classList.add('--is-visible')
  searchInput.focus()
}

function closeSearch() {
  isSearchOpen = false

  searchToggle.classList.remove('--is-active')
  globalSearchFilter.classList.remove('--is-visible')
}

function openAuthDropdown() {
  if (authDropdown) {
    closeSearch()
    closeAllNavMenus()

    isAuthOpen = true

    authDropdownToggle.classList.add('--is-active')
    authDropdown.classList.add('--is-visible')
  }
}

function closeAuthDropdown() {
  isAuthOpen = false

  if (authDropdown) {
    authDropdown.classList.remove('--is-visible')
    authDropdownToggle.classList.remove('--is-active')
  }
}

function toggleAuthDropdown() {
  // Do nothing if dd isn't being displayed
  if (!authDropdown) {
    return
  }

  if (isAuthOpen) {
    return closeAuthDropdown()
  }

  openAuthDropdown()
}

// --------------[Event Handling]----------------- //

const debouncedOpen = debounce(openFlyout, 200)
const openFlyoutWithDelay = e => debouncedOpen(e, e.currentTarget, true)

const closeNavMenusIfUnhovered = debounce(() => {
  if (!isHovering([...navCategories, ...navCategoryFlyouts])) {
    closeAllNavMenus()
  }
}, 70)

const closeNavMenusIfUnfocused = debounce(() => {
  if (
    !isHovering([...navCategories, ...navCategoryFlyouts]) &&
    !isFocusing([...navCategories, ...navSubcategoryItems])
  ) {
    closeAllNavMenus()
  }
}, 70)

const closeAuthDropdownIfUnhovered = debounce(() => {
  if (!isHovering([authDropdownToggle, authDropdown])) {
    closeAuthDropdown()
  }
}, 70)

function bindMobileListeners() {
  hamburger.addEventListener('click', toggleDropdown)

  navOverlay.addEventListener('click', toggleDropdown)

  for (let i = 0; i < navCategories.length; i++) {
    navCategories[i].addEventListener('click', openFlyout)
    navCategories[i].addEventListener('focus', openFlyout)
  }

  for (let i = 0; i < backToMainBtns.length; i++) {
    backToMainBtns[i].addEventListener('click', closeFlyout)
  }

  if (authDropdownToggle) {
    authDropdownToggle.addEventListener('click', toggleAuthDropdown)
  }
}

function unbindMobileListeners() {
  hamburger.removeEventListener('click', toggleDropdown)

  navOverlay.removeEventListener('click', toggleDropdown)

  for (let i = 0; i < navCategories.length; i++) {
    navCategories[i].removeEventListener('click', openFlyout)
    navCategories[i].removeEventListener('focus', openFlyout)
  }

  for (let i = 0; i < backToMainBtns.length; i++) {
    backToMainBtns[i].removeEventListener('click', closeFlyout)
  }

  if (authDropdownToggle) {
    authDropdownToggle.removeEventListener('click', toggleAuthDropdown)
  }
}

function bindDesktopListeners() {
  for (let i = 0; i < navCategories.length; i++) {
    navCategories[i].addEventListener('mouseenter', openFlyoutWithDelay)
    navCategories[i].addEventListener('mouseout', closeNavMenusIfUnhovered)
    navCategories[i].addEventListener('focus', openFlyoutWithDelay)
    navCategories[i].addEventListener('blur', closeNavMenusIfUnfocused)
  }

  for (let i = 0; i < navSubcategoryItems.length; i++) {
    navSubcategoryItems[i].addEventListener('blur', closeNavMenusIfUnfocused)
  }

  for (let i = 0; i < navCategoryFlyouts.length; i++) {
    navCategoryFlyouts[i].addEventListener('mouseout', closeNavMenusIfUnhovered)
  }

  searchSubmit.addEventListener('blur', closeSearch)
  searchInput.addEventListener('keydown', function(event) {
    if (event.keyCode === 27) {
      closeSearch()
    }
  })
}

function unbindDesktopListeners() {
  navCategories.forEach(category => {
    category.removeEventListener('mouseenter', openFlyoutWithDelay)
    category.removeEventListener('mouseout', closeNavMenusIfUnhovered)
    category.removeEventListener('focus', openFlyoutWithDelay)
    category.removeEventListener('blur', closeNavMenusIfUnfocused)
  })

  navSubcategoryItems.forEach(subcatItem => {
    subcatItem.removeEventListener('blur', closeNavMenusIfUnfocused)
  })

  navCategoryFlyouts.forEach(flyout => {
    flyout.removeEventListener('mouseout', closeNavMenusIfUnhovered)
  })
}

function responsiveListeners(desktopMatcher) {
  closeSearch()
  closeAllNavMenus()
  closeAuthDropdown()

  if (desktopMatcher.matches) {
    unbindMobileListeners()
    return bindDesktopListeners()
  }

  unbindDesktopListeners()
  bindMobileListeners()
}

function initListeners() {
  desktopMatcher.addListener(responsiveListeners)

  searchToggle.addEventListener('click', toggleSearch)

  // Click out to close search
  document.addEventListener('click', function(event) {
    const isClickInside =
      searchToggle.contains(event.target) ||
      globalSearchFilter.contains(event.target)

    if (!isClickInside && isSearchOpen) {
      closeSearch()
    }
  })

  if (authDropdownToggle) {
    authDropdownToggle.removeEventListener('mouseenter', openAuthDropdown)
    authDropdownToggle.removeEventListener('mouseout', closeNavMenusIfUnhovered)
    authDropdown.removeEventListener('mouseout', closeAuthDropdownIfUnhovered)
  }

  if (desktopMatcher.matches) {
    return bindDesktopListeners()
  }

  bindMobileListeners()
}

initListeners()
